import { useQuery, useQueryClient } from "@tanstack/react-query"
import { useCallback, useMemo } from "react"

import type { List } from "@/api/lists"
import type { DenormalizedTargetAttribute } from "@/api/templates"
import type { DenormalizedWorkspace } from "@/api/workspaces"
import { queryKeys } from "@/queries/keys"

// Lists are nested in the workspaces viewData payload. They are seeded into the
// query cache from bootstrap viewData by `hydrateQueriesFromViewData` (non-slim)
// or by the deferred workspaces fetcher (slim), both via `listsFromWorkspaces`
// below, so the seeded data is authoritative and mutations keep the cache
// current instead of refetching. There is no standalone lists endpoint, so the
// queryFn only guards render paths that mount without seeded data.
const EMPTY_LISTS: List[] = []

/** Flatten the lists nested in a workspaces payload into the flat cache shape. */
export function listsFromWorkspaces(workspaces: DenormalizedWorkspace[]): List[] {
  return workspaces.flatMap((workspace) => workspace.lists ?? [])
}

function useListsData(): List[] {
  const { data } = useQuery({
    queryKey: queryKeys.lists.all,
    queryFn: () => EMPTY_LISTS,
    staleTime: Infinity,
  })
  return data ?? EMPTY_LISTS
}

// Keyed on the cache array reference so the id lookup is built once per data
// version and shared across consumers. React Query hands back the same array
// reference until a cache writer replaces it, so consumers keep stable object
// identities between renders.
const byIdByListRef = new WeakMap<List[], Record<number, List | undefined>>()

function listsById(lists: List[]): Record<number, List | undefined> {
  const cached = byIdByListRef.get(lists)
  if (cached) {
    return cached
  }
  const map: Record<number, List | undefined> = {}
  for (const list of lists) {
    map[list.id] = list
  }
  byIdByListRef.set(lists, map)
  return map
}

export const useAllLists = (): List[] => useListsData()

export const useLists = (): Record<number, List | undefined> => {
  const lists = useListsData()
  return useMemo(() => listsById(lists), [lists])
}

export const useListById = (id?: number): List | undefined => {
  const byId = useLists()
  return id !== undefined ? byId[id] : undefined
}

export const useWorkspaceLists = (workspaceId: number): List[] | undefined => {
  const allLists = useAllLists()
  return useMemo(
    () => allLists.filter((list) => list && list.workspaceId === workspaceId),
    [allLists, workspaceId],
  )
}

export const useListsByIds = (ids: number[]): (List | undefined)[] => {
  const byId = useLists()
  return useMemo(() => ids.map((id) => byId[id]), [byId, ids])
}

/**
 * Cache writers for list mutations. Call sites update the `lists` query cache
 * directly so the shared list stays current without a refetch.
 *
 * The cascade writers (`onTemplateArchived`, `onTargetAttributeUpdated`,
 * `onTargetAttributeDeleted`, `onWorkspaceDeleted`) keep the flat lists cache
 * consistent with template/target-attribute/workspace mutations: lists embed
 * denormalized `templateId` and `listAttributes.targetAttribute`, so those
 * mutation call sites must mirror the change into any referencing list.
 */
export function useListsCache() {
  const queryClient = useQueryClient()

  // `queryClient` is stable, so these writers keep a stable identity across
  // renders and are safe to list in effect/`useAsync` dependency arrays.
  const writeLists = useCallback(
    (updater: (previous: List[]) => List[]) => {
      queryClient.setQueryData<List[]>(queryKeys.lists.all, (previous) =>
        updater(previous ?? []),
      )
    },
    [queryClient],
  )

  // Adds lists that are not already cached; existing entries are left untouched
  // (callers needing to refresh an already-cached list should use `updateList`).
  const createLists = useCallback(
    (lists: List[]) => {
      writeLists((previous) => {
        const existing = new Set(previous.map((list) => list.id))
        const additions = lists.filter((list) => !existing.has(list.id))
        return additions.length > 0 ? [...previous, ...additions] : previous
      })
    },
    [writeLists],
  )

  const updateList = useCallback(
    (changes: Partial<List>) => {
      if (changes.id === undefined) {
        return
      }
      writeLists((previous) =>
        previous.map((list) => (list.id === changes.id ? { ...list, ...changes } : list)),
      )
    },
    [writeLists],
  )

  const moveListWorkspace = useCallback(
    (listId: number, newWorkspaceId: number) => {
      writeLists((previous) =>
        previous.map((list) =>
          list.id === listId ? { ...list, workspaceId: newWorkspaceId } : list,
        ),
      )
    },
    [writeLists],
  )

  const removeList = useCallback(
    (listId: number) => {
      writeLists((previous) => previous.filter((list) => list.id !== listId))
    },
    [writeLists],
  )

  const onTemplateArchived = useCallback(
    (templateId: number) => {
      writeLists((previous) =>
        previous.map((list) =>
          list.templateId === templateId ? { ...list, templateId: undefined } : list,
        ),
      )
    },
    [writeLists],
  )

  const onTargetAttributeUpdated = useCallback(
    (targetAttribute: DenormalizedTargetAttribute) => {
      writeLists((previous) =>
        previous.map((list) => {
          if (
            !list.listAttributes?.some(
              (la) => la.targetAttribute?.id === targetAttribute.id,
            )
          ) {
            return list
          }
          return {
            ...list,
            listAttributes: list.listAttributes.map((la) =>
              la.targetAttribute?.id === targetAttribute.id
                ? { ...la, targetAttribute }
                : la,
            ),
          }
        }),
      )
    },
    [writeLists],
  )

  const onTargetAttributeDeleted = useCallback(
    (templateId: number, targetAttributeId: number) => {
      writeLists((previous) =>
        previous.map((list) => {
          if (list.templateId !== templateId) {
            return list
          }
          if (
            !list.listAttributes?.some(
              (la) => la.targetAttribute?.id === targetAttributeId,
            )
          ) {
            return list
          }
          return {
            ...list,
            listAttributes: list.listAttributes.map((la) =>
              la.targetAttribute?.id === targetAttributeId
                ? { ...la, targetAttribute: undefined }
                : la,
            ),
          }
        }),
      )
    },
    [writeLists],
  )

  const onWorkspaceDeleted = useCallback(
    (workspaceId: number) => {
      writeLists((previous) =>
        previous.filter((list) => list.workspaceId !== workspaceId),
      )
    },
    [writeLists],
  )

  return useMemo(
    () => ({
      createLists,
      updateList,
      moveListWorkspace,
      removeList,
      onTemplateArchived,
      onTargetAttributeUpdated,
      onTargetAttributeDeleted,
      onWorkspaceDeleted,
    }),
    [
      createLists,
      updateList,
      moveListWorkspace,
      removeList,
      onTemplateArchived,
      onTargetAttributeUpdated,
      onTargetAttributeDeleted,
      onWorkspaceDeleted,
    ],
  )
}
